import React, { useState, useEffect, useCallback } from "react";
import { fetchApi } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Upload, Trash2, Save, Image, Search } from "lucide-react";

function AssetTypes() {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("assets.review");
    const [classifications, setClassifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [actionMsg, setActionMsg] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editNotes, setEditNotes] = useState("");
    const [uploadingId, setUploadingId] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [photoUrls, setPhotoUrls] = useState({});
    const [showAddModal, setShowAddModal] = useState(false);
    const [mcSearch, setMcSearch] = useState("");
    const [mcResults, setMcResults] = useState([]);
    const [mcLoading, setMcLoading] = useState(false);
    const [addingId, setAddingId] = useState(null);

    const showMsg = (msg, type = "ok") => {
        setActionMsg({ text: msg, type });
        setTimeout(() => setActionMsg(null), 3000);
    };

    const loadClassifications = useCallback(async () => {
        try {
            const data = await fetchApi("/assets/classifications");
            setClassifications(data.classifications || []);

            // Use SAS URLs generated server-side (now arrays)
            const urls = {};
            for (const c of (data.classifications || [])) {
                if (c.placement_photo_urls && c.placement_photo_urls.length > 0) {
                    urls[c.id] = c.placement_photo_urls;
                }
            }
            setPhotoUrls(urls);
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadClassifications(); }, [loadClassifications]);

    const handleSaveNotes = async (id) => {
        try {
            await fetchApi(`/assets/classifications/${id}`, {
                method: "PUT",
                body: JSON.stringify({ placementNotes: editNotes }),
            });
            showMsg("Placement notes saved");
            setEditingId(null);
            loadClassifications();
        } catch (err) {
            showMsg(err.message, "err");
        }
    };

    const handleToggleActive = async (c) => {
        try {
            await fetchApi(`/assets/classifications/${c.id}`, {
                method: "PUT",
                body: JSON.stringify({ placementNotes: c.placement_notes, active: !c.active }),
            });
            showMsg(c.active ? `${c.name} hidden` : `${c.name} shown`);
            loadClassifications();
        } catch (err) {
            showMsg(err.message, "err");
        }
    };

    const handlePhotoUpload = async (id, file) => {
        setUploadingId(id);
        try {
            const form = new FormData();
            form.append("photo", file);
            const token = await getToken();
            const resp = await fetch(`/api/v1/assets/classifications/${id}/photo`, {
                method: "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: form,
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || "Upload failed");
            showMsg("Placement photo uploaded");
            if (data.url) {
                setPhotoUrls(prev => ({
                    ...prev,
                    [id]: [...(prev[id] || []), data.url],
                }));
            }
            loadClassifications();
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setUploadingId(null);
        }
    };

    const handleDeletePhoto = async (id, index) => {
        if (!window.confirm("Remove this placement photo?")) return;
        try {
            await fetchApi(`/assets/classifications/${id}/photo?index=${index}`, { method: "DELETE" });
            showMsg("Photo removed");
            setPhotoUrls(prev => {
                const arr = [...(prev[id] || [])];
                arr.splice(index, 1);
                if (arr.length === 0) { const n = { ...prev }; delete n[id]; return n; }
                return { ...prev, [id]: arr };
            });
            loadClassifications();
        } catch (err) {
            showMsg(err.message, "err");
        }
    };

    // Token helper for multipart upload (can't use fetchApi for FormData)
    const getToken = async () => {
        try {
            const { msalInstance } = await import("../index");
            const { apiRequest } = await import("../authConfig");
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length === 0) return null;
            const resp = await msalInstance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
            return resp.accessToken;
        } catch { return null; }
    };

    const searchMcClassifications = async (term) => {
        setMcLoading(true);
        try {
            const data = await fetchApi(`/assets/mc-classifications?search=${encodeURIComponent(term)}`);
            setMcResults(data.classifications || []);
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setMcLoading(false);
        }
    };

    const handleAddClassification = async (cls) => {
        setAddingId(cls.id);
        try {
            await fetchApi("/assets/classifications", {
                method: "POST",
                body: JSON.stringify({ classificationId: cls.id, name: cls.name }),
            });
            showMsg(`${cls.name} added`);
            // Mark as added in results
            setMcResults(prev => prev.map(c => c.id === cls.id ? { ...c, already_added: true } : c));
            loadClassifications();
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setAddingId(null);
        }
    };

    const filtered = classifications.filter(c => {
        if (!search) return true;
        const s = search.toLowerCase();
        return c.name.toLowerCase().includes(s) || c.classification_id.toLowerCase().includes(s);
    });

    const activeCount = classifications.filter(c => c.active).length;
    const withPhoto = classifications.filter(c => c.placement_photo).length;

    if (loading) return <div style={{ padding: 24, color: "#888" }}>Loading asset types...</div>;

    return (
        <div style={{ padding: "16px 24px", maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                    <h2 style={{ margin: "0 0 4px" }}>Asset Types</h2>
                    <p style={{ color: "#888", margin: 0, fontSize: 14 }}>
                        Manage barcode placement photos and instructions for each asset classification.
                        {" "}{activeCount} active types, {withPhoto} with placement photos.
                    </p>
                </div>
                {canEdit && (
                    <button
                        onClick={() => { setShowAddModal(true); setMcSearch(""); setMcResults([]); }}
                        style={{
                            padding: "8px 16px", background: "#2563eb", color: "#fff",
                            border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
                            fontWeight: 600, whiteSpace: "nowrap",
                        }}
                    >
                        + Add Type
                    </button>
                )}
            </div>

            {actionMsg && (
                <div style={{
                    padding: "8px 14px", borderRadius: 6, marginBottom: 12, fontSize: 14,
                    background: actionMsg.type === "err" ? "#fef2f2" : "#f0fdf4",
                    color: actionMsg.type === "err" ? "#dc2626" : "#16a34a",
                    border: `1px solid ${actionMsg.type === "err" ? "#fecaca" : "#bbf7d0"}`,
                }}>
                    {actionMsg.text}
                </div>
            )}

            {/* Search */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
                <div style={{ position: "relative", flex: 1, maxWidth: 300 }}>
                    <Search size={16} style={{ position: "absolute", left: 10, top: 9, color: "#aaa" }} />
                    <input
                        type="text"
                        placeholder="Search types..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            width: "100%", padding: "8px 10px 8px 32px", border: "1px solid #d1d5db",
                            borderRadius: 6, fontSize: 14, boxSizing: "border-box",
                        }}
                    />
                </div>
                <span style={{ fontSize: 13, color: "#888" }}>{filtered.length} types</span>
            </div>

            {/* Table */}
            <div style={{ maxHeight: "calc(100vh - 240px)", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                    <thead>
                        <tr style={{ background: "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                            <th style={thStyle}>Type</th>
                            <th style={{ ...thStyle, width: 60, textAlign: "center" }}>Count</th>
                            <th style={thStyle}>Placement Photo</th>
                            <th style={thStyle}>Instructions</th>
                            {canEdit && <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(c => (
                            <tr key={c.id} style={{
                                borderBottom: "1px solid #f1f5f9",
                                opacity: c.active ? 1 : 0.5,
                            }}>
                                {/* Type name + code */}
                                <td style={tdStyle}>
                                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                                    <div style={{ fontSize: 12, color: "#999" }}>{c.classification_id}</div>
                                </td>

                                {/* MC asset count */}
                                <td style={{ ...tdStyle, textAlign: "center", color: "#888" }}>
                                    {c.mc_asset_count}
                                </td>

                                {/* Placement photos (multiple) */}
                                <td style={tdStyle}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                        {(photoUrls[c.id] || []).map((url, idx) => (
                                            <div key={idx} style={{ position: "relative" }}>
                                                <div
                                                    onClick={() => setPreviewImage(url)}
                                                    style={{
                                                        width: 60, height: 45, borderRadius: 6, overflow: "hidden",
                                                        border: "1px solid #e2e8f0", cursor: "pointer",
                                                        background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}
                                                >
                                                    <img src={url} alt={`${c.name} ${idx + 1}`}
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                </div>
                                                {canEdit && (
                                                    <button
                                                        onClick={() => handleDeletePhoto(c.id, idx)}
                                                        title="Remove photo"
                                                        style={{
                                                            position: "absolute", top: -6, right: -6,
                                                            background: "#ef4444", color: "#fff", border: "none",
                                                            borderRadius: "50%", width: 18, height: 18, fontSize: 11,
                                                            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                                                            lineHeight: 1, padding: 0,
                                                        }}
                                                    >
                                                        &times;
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {canEdit && (
                                            <label style={{
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                width: 60, height: 45, background: "#f1f5f9", border: "1px dashed #cbd5e1",
                                                borderRadius: 6, cursor: "pointer", color: "#64748b",
                                            }}>
                                                {uploadingId === c.id ? "..." : <Upload size={16} />}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: "none" }}
                                                    disabled={uploadingId === c.id}
                                                    onChange={e => {
                                                        if (e.target.files[0]) handlePhotoUpload(c.id, e.target.files[0]);
                                                    }}
                                                />
                                            </label>
                                        )}
                                        {!canEdit && (!photoUrls[c.id] || photoUrls[c.id].length === 0) && (
                                            <span style={{ color: "#ccc", fontSize: 13 }}>No photos</span>
                                        )}
                                    </div>
                                </td>

                                {/* Placement notes */}
                                <td style={tdStyle}>
                                    {editingId === c.id ? (
                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                            <textarea
                                                value={editNotes}
                                                onChange={e => setEditNotes(e.target.value)}
                                                placeholder="e.g. Place barcode on the left side panel, below the serial number plate"
                                                rows={2}
                                                style={{
                                                    flex: 1, padding: "6px 8px", border: "1px solid #d1d5db",
                                                    borderRadius: 6, fontSize: 13, resize: "vertical",
                                                }}
                                            />
                                            <button onClick={() => handleSaveNotes(c.id)} title="Save" style={iconBtnStyle}>
                                                <Save size={14} color="#22c55e" />
                                            </button>
                                            <button onClick={() => setEditingId(null)} style={iconBtnStyle}>×</button>
                                        </div>
                                    ) : (
                                        <div
                                            onClick={() => { if (canEdit) { setEditingId(c.id); setEditNotes(c.placement_notes || ""); } }}
                                            style={{
                                                cursor: canEdit ? "pointer" : "default",
                                                color: c.placement_notes ? "#333" : "#ccc",
                                                fontSize: 13,
                                                minHeight: 20,
                                            }}
                                            title={canEdit ? "Click to edit" : ""}
                                        >
                                            {c.placement_notes || (canEdit ? "Click to add instructions..." : "—")}
                                        </div>
                                    )}
                                </td>

                                {/* Actions */}
                                {canEdit && (
                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                        <button
                                            onClick={() => handleToggleActive(c)}
                                            style={{
                                                padding: "4px 10px", fontSize: 12, borderRadius: 4,
                                                border: "1px solid #e2e8f0", cursor: "pointer",
                                                background: c.active ? "#fff" : "#f0fdf4",
                                                color: c.active ? "#888" : "#16a34a",
                                            }}
                                        >
                                            {c.active ? "Hide" : "Show"}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add Asset Type modal */}
            {showAddModal && (
                <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 9998, padding: 16,
                }}>
                    <div style={{
                        background: "#fff", borderRadius: 12, padding: 24,
                        width: "100%", maxWidth: 500, maxHeight: "80vh", display: "flex", flexDirection: "column",
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <h3 style={{ margin: 0 }}>Add Asset Type from MC</h3>
                            <button onClick={() => setShowAddModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>&times;</button>
                        </div>
                        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                            <input
                                type="text"
                                value={mcSearch}
                                onChange={e => setMcSearch(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") searchMcClassifications(mcSearch); }}
                                placeholder="Search MC classifications..."
                                style={{
                                    flex: 1, padding: "8px 12px", border: "1px solid #d1d5db",
                                    borderRadius: 6, fontSize: 14,
                                }}
                            />
                            <button
                                onClick={() => searchMcClassifications(mcSearch)}
                                disabled={mcLoading}
                                style={{
                                    padding: "8px 16px", background: "#2563eb", color: "#fff",
                                    border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
                                }}
                            >
                                {mcLoading ? "..." : "Search"}
                            </button>
                        </div>
                        <div style={{ overflowY: "auto", flex: 1 }}>
                            {mcResults.length === 0 && !mcLoading && (
                                <div style={{ textAlign: "center", padding: 24, color: "#888", fontSize: 14 }}>
                                    Search for a classification in Maintenance Connection to add it.
                                </div>
                            )}
                            {mcResults.map(c => (
                                <div key={c.id} style={{
                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                    padding: "8px 12px", borderBottom: "1px solid #f1f5f9",
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                                        <div style={{ fontSize: 12, color: "#888" }}>{c.id}</div>
                                    </div>
                                    {c.already_added ? (
                                        <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 600 }}>Added</span>
                                    ) : (
                                        <button
                                            onClick={() => handleAddClassification(c)}
                                            disabled={addingId === c.id}
                                            style={{
                                                padding: "4px 12px", background: "#eff6ff", color: "#2563eb",
                                                border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer",
                                                fontSize: 13, fontWeight: 600,
                                            }}
                                        >
                                            {addingId === c.id ? "Adding..." : "Add"}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Full-size image preview modal */}
            {previewImage && (
                <div
                    onClick={() => setPreviewImage(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 9999, cursor: "pointer",
                    }}
                >
                    <img
                        src={previewImage}
                        alt="Barcode placement"
                        style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 8 }}
                    />
                </div>
            )}
        </div>
    );
}

const thStyle = {
    padding: "10px 12px", textAlign: "left", fontWeight: 600, fontSize: 13,
    borderBottom: "2px solid #e2e8f0", color: "#555",
};

const tdStyle = {
    padding: "10px 12px", verticalAlign: "middle",
};

const iconBtnStyle = {
    background: "none", border: "none", cursor: "pointer", padding: 4,
    borderRadius: 4, display: "flex", alignItems: "center",
};

export default AssetTypes;
